import {
  Archive,
  EllipsisVertical,
  ListOrdered,
  LucideIcon,
  Pen,
  RefreshCw,
} from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { ArchiveCaseAlertDialog } from "~/features/cases/components/ArchiveCaseAlertDialog";
import { ROUTES, useOrganizationParams } from "~/helpers";
import { IndexStatusResponse } from "~/lib/knowledge/reindex";
import { trackEvent } from "~/utils/analytics";
import { EditCaseDialog } from "./EditCaseDialog";
import { IndexStatusBadge } from "./IndexStatusBadge";

interface CaseActionsProps {
  caseId: string;
  indexStatus: IndexStatusResponse | undefined;
  isReindexing: boolean;
  onReindex: () => void;
  fileCount: number;
}

interface DropdownItem {
  icon: LucideIcon;
  messageId: string;
  defaultMessage: string;
  onClick: () => void;
  disabled?: boolean;
}

export const CaseActions: FC<CaseActionsProps> = ({
  caseId,
  indexStatus,
  isReindexing,
  onReindex,
  fileCount,
}) => {
  const navigate = useNavigate();
  const { organizationId } = useOrganizationParams();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);

  const handleArchived = () => {
    navigate(ROUTES.CASES({ organizationId }));
  };

  const isIndexed =
    indexStatus &&
    (indexStatus.index_status === "indexed" ||
      indexStatus.index_status === "completed");

  const isIndexing =
    indexStatus &&
    (indexStatus.index_status === "indexing" ||
      indexStatus.index_status === "pending");

  useEffect(() => {
    if (dropdownOpen) {
      trackEvent({ eventName: "app/case_actions.dropdown/open" });
    }
  }, [dropdownOpen]);

  const dropdownItems: DropdownItem[] = [
    {
      icon: Pen,
      messageId: "edit",
      defaultMessage: "Edit",
      onClick: () => {
        trackEvent({ eventName: "app/case_actions_menu/edit.link/click" });
        setShowEditDialog(true);
        setDropdownOpen(false);
      },
    },
    {
      icon: Archive,
      messageId: "archive",
      defaultMessage: "Archive",
      onClick: () => {
        trackEvent({ eventName: "app/case_actions_menu/archive.link/click" });
        setShowArchiveDialog(true);
        setDropdownOpen(false);
      },
    },
  ];

  const handleIndexClick = () => {
    trackEvent({ eventName: "app/case_actions_menu/index.link/click" });
    onReindex();
    setDropdownOpen(false);
  };

  const isIndexDisabled = isReindexing || isIndexing;

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-6 w-6">
            <EllipsisVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="min-w-[160px] w-auto" align="start">
          {dropdownItems.map((item) => (
            <DropdownMenuItem
              key={item.messageId}
              onClick={item.disabled ? undefined : item.onClick}
              disabled={item.disabled}
            >
              <item.icon className="mr-2 h-4 w-4" />
              <FormattedMessage
                id={item.messageId}
                defaultMessage={item.defaultMessage}
              />
            </DropdownMenuItem>
          ))}
          {fileCount > 0 && (
            <DropdownMenuItem
              onClick={isIndexDisabled ? undefined : handleIndexClick}
              disabled={isIndexDisabled}
              className="flex-col items-start"
            >
              <div className="flex items-center">
                {isIndexed ? (
                  <RefreshCw
                    className={`mr-2 h-4 w-4 ${isReindexing ? "animate-spin" : ""}`}
                  />
                ) : (
                  <ListOrdered className="mr-2 h-4 w-4" />
                )}
                <FormattedMessage
                  id={isIndexed ? "case.reindex" : "case.index"}
                  defaultMessage={isIndexed ? "Reindex" : "Index"}
                />
              </div>
              <div className="ml-6 mt-1 text-xs">
                <IndexStatusBadge status={indexStatus} hideIcon />
              </div>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {showEditDialog && (
        <EditCaseDialog
          open={showEditDialog}
          setOpen={setShowEditDialog}
          projectId={caseId}
        />
      )}

      {showArchiveDialog && (
        <ArchiveCaseAlertDialog
          open={showArchiveDialog}
          setOpen={setShowArchiveDialog}
          projectId={caseId}
          onArchived={handleArchived}
        />
      )}
    </>
  );
};
